import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styles from "../Pages/PagesStyles/AdminRepairEntry.module.css";
import { toPlainFields } from "Components/support/Allfields/fieldconverter";
import { PurchaseEntryNewFields } from "Components/support/Allfields/purchasefields";
import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import GetApiService from "Components/Api/getapiService";
import Select from "react-select";
import { FaDollarSign } from "react-icons/fa";
import { FiRefreshCcw } from "react-icons/fi";
import {
  DiamondFields,
  GeneralFields,
  generateDropDownList,
  StoneFields,
} from "utils/helpers";
import SingleStonePopUp from "Components/support/purchasesupport/SingleStonePopUp";
import SingleDiamondPopUpNew from "./SingleDiamondPopUpNew";
import { Add } from "@mui/icons-material";
import SingleStonePopUpNewForPurchase from "./SignleStonePopUpNewForPurchase";

const selectStyle = {
  control: (base) => ({
    ...base,
    minHeight: "35px",
    height: "30px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#aaa",
    fontStyle: "italic",
    display: "none",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "blue"
      : state.isFocused
      ? "lightblue"
      : "white",
    color: state.isSelected ? "white" : "black",
    padding: "6px",
    fontSize: "12px",
    whiteSpace: "normal", // Allows text to wrap instead of truncating
    textOverflow: "unset", // Removes ellipsis
    overflow: "visible", // Ensures full visibility of text
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
    padding: "2px 2px 2px 0px", // Decreases padding inside the input box
    whiteSpace: "normal", // Allows full text to display
    textOverflow: "unset", // Removes ellipsis
    overflow: "visible", // Ensures full visibility of text
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    marginTop: "5px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    svg: {
      width: "12px",
      height: "12px",
    },
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: "0px",
  }),
};

const SkuFormRepair = ({
  skus,
  isInvoiceEntry,
  itemfields,
  editValues,
  edit,
  updateSku,
  addSku,
  isSummaryOpen,
  setIsSummaryOpen,
  apifields,
  setItemfields,
  setCategory,
  VendorName,
  vendorTounchList,
  invoiceNumber,
}) => {
  const allStates = useSelector((state) => state);
  const adminLoggedIn = allStates.reducer1;
  const clientCode = adminLoggedIn.ClientCode;
  const apiService = new GetApiService(clientCode);
  const fetchAllData = async () => {
    try {
      const [
        allSku,
        categories,
        productTypes,
        designs,
        purities,
        diamondAttributes,
        stoneAttributes,
      ] = await Promise.allSettled([
        apiService.fetchAllSku(),
        apiService.fetchAllCategories(),
        apiService.fetchAllProductType(),
        apiService.fetchAllDesigns(),
        apiService.fetchAllPurities(),
        apiService.fetchAllDiamondAttributes(),
        apiService.fetchAllStoneAttributes(),
      ]);

      // Check the results of each promise and handle errors if any
      const results = {
        allSku: allSku.status === "fulfilled" ? allSku.value : null,
        categories: categories.status === "fulfilled" ? categories.value : null,
        productTypes:
          productTypes.status === "fulfilled" ? productTypes.value : null,
        designs: designs.status === "fulfilled" ? designs.value : null,
        purities: purities.status === "fulfilled" ? purities.value : null,
        diamondAttributes:
          diamondAttributes.status === "fulfilled"
            ? diamondAttributes.value
            : null,
        stoneAttributes:
          stoneAttributes.status === "fulfilled" ? stoneAttributes.value : null,
      };

      // Log and handle any rejected promises (optional)
      const failedPromises = [
        allSku,
        categories,
        productTypes,
        designs,
        purities,
        diamondAttributes,
        stoneAttributes,
      ].filter((result) => result.status === "rejected");

      if (failedPromises.length > 0) {
        failedPromises.forEach((failedPromise) => {
          console.error(`Failed to fetch data: ${failedPromise.reason}`);
        });
      }

      return results;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw new Error(`Failed to fetch dropdown list data: ${error.message}`);
    }
  };
  const {
    data: dropdownListData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["allData"],
    queryFn: fetchAllData,
    refetchOnWindowFocus: false,
    // cacheTime: 1000 * 60 * 2,
    // staleTime: 1000 * 60 * 2,
    onError: (err) => {
      console.error("Query Error:", err);
    },
  });

  const [isToggled, setIsToggled] = useState(false);
  const [changeWastageWt, setChangeWastageWt] = useState(false);
  const [changeRate, setChangeRate] = useState(false);
  const [popup, setPopUp] = useState();
  const [isGrossWeightChanged, setIsGrossWeightChanged] = useState(false);
  const [isWastageFieldDisabled, setIsWastageFieldDisabled] = useState(false);
  const fieldRefs = useRef([]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: edit
      ? editValues
      : {
          DiamondAmount: 0,
          Diamonds: [],
          Stones: [],
        },
  });

  const StockKeepingUnit = watch("StockKeepingUnit");
  const CategoryId = watch("CategoryId");
  const ProductId = watch("ProductId");
  const DesignId = watch("DesignId");
  const PurityId = watch("PurityId");
  const TotalWt = watch("TotalWt");
  const PackingWeight = watch("PackingWeight");
  const GrossWt = watch("GrossWt");
  const StoneWt = watch("StoneWt");
  const DiamondWeight = watch("DiamondWeight");
  const FinePercent = watch("FinePercent");
  const WastagePercent = watch("WastagePercent");
  const Hallmark = watch("Hallmark");
  const NetWt = watch("NetWt");
  const FineWt = watch("FineWt");
  const WastageWt = watch("WastageWt");
  const MetalRate = watch("MetalRate");
  const MakingPerGram = watch("MakingPerGram");
  const MakingFixedWastage = watch("MakingFixedWastage");
  const MakingFixedAmt = watch("MakingFixedAmt");
  const FineWastageWt = watch("FineWastageWt");
  const MetalAmount = watch("MetalAmount");
  const TotalMakingAmount = watch("TotalMakingAmount");
  const StoneAmount = watch("StoneAmount");

  const DiamondAmount = watch("DiamondAmount");
  const DiamondName = watch("DiamondName");
  console.log(DiamondName)
  const Stones = watch("Stones");
  const Diamonds = watch("Diamonds");
  const RateCut = watch("RateCut");
  const WastageCut = watch("WastageCut");
  const Qty = watch("Qty");
  const Pieces = watch("Pieces");
  const ClipQty = watch("ClipQty");
  const ClipWt = watch("ClipWt");
  const TagWt = watch("TagWt");
  const FindingWt = watch("FindingWt");
  const LanyardWt = watch("LanyardWt");
  const PouchWt = watch("PouchWt");
  const StonePieces = watch("StonePieces");
  const LooseDiamondWeight = watch("DiamondWeight");
  const LooseDiamondRate = watch("DiamondRate");
  const LooseStoneWeight = watch("StoneWeight");
  const LooseStonePieces = watch("StonePieces");
  const StoneRateCt = watch("StoneRateCt");
  const StoneRatePiece = watch("StoneRatePc");
  const localfields = useMemo(() => toPlainFields(PurchaseEntryNewFields), []);
  const [selectedSku, setSelectedSku] = useState();


  useEffect(() => {
    if (invoiceNumber) {
      setValue("RateCut", true);
    } else {
      setValue("RateCut", false);
    }
  }, [invoiceNumber]);

  useEffect(() => {
    if (!edit && !StockKeepingUnit) {
      setValue("ProductId", "");
      setValue("DesignId", "");
      setValue("PurityId", "");
    }
  }, [CategoryId?.value, StockKeepingUnit, edit]);

  useEffect(() => {
    if (!edit && !StockKeepingUnit) {
      setValue("DesignId", "");
      setValue("PurityId", "");
    }
  }, [ProductId?.value, StockKeepingUnit, edit]);

  useEffect(() => {
    if (!edit && !StockKeepingUnit) {
      setValue("PurityId", "");
    }
  }, [DesignId?.value, StockKeepingUnit, edit]);

  useEffect(() => {
    setCategory(CategoryId?.value);
    // resetDiamondSection()
  }, [CategoryId]);

  useEffect(() => {
    if (StockKeepingUnit?.value) {
      const sku = dropdownListData?.allSku?.find(
        (item) => item?.Id == StockKeepingUnit.value
      );
      setSelectedSku(sku);
      setValue("CategoryId", {
        value: sku?.CategoryId,
        label: sku?.CategoryName,
      });
      setValue("ProductId", { value: sku?.ProductId, label: sku?.ProductName });
      setValue("DesignId", { value: sku?.DesignId, label: sku?.DesignName });
      setValue("PurityId", { value: sku?.PurityId, label: sku?.PurityName });
      // setValue("StonePieces", +sku?.TotalStonePieces);

      console.log(sku)
      // setValue("StoneAmount", +sku?.TotalStoneAmount);
      if (
        sku?.SKUVendor?.length &&
        sku?.SKUVendor?.filter((item) => item?.VendorId == VendorName?.value)
          ?.length
      ) {
        setValue("ClipWt", +sku?.ClipWeight);
        setValue("TagWt", +sku?.TagWeight);
        setValue("FindingWt", +sku?.FindingWeight);
        setValue("LanyardWt", +sku?.LanyardWeight);
        setValue("Pieces", +sku?.Pieces);
        
        // setValue(
        //   "PackingWeight",
        //   (
        //     +(sku?.LanyardWeight || 0) +
        //     +(sku?.FindingWeight || 0) +
        //     +(sku?.TagWeight || 0) +
        //     +(sku?.ClipWeight || 0)
        //   )?.toFixed(3)
        // );
      } else {
        setValue("ClipWt", "");
        setValue("TagWt", "");
        setValue("FindingWt", "");
        setValue("LanyardWt", "");
      }
    }
  }, [StockKeepingUnit?.value, VendorName?.value]);

  useEffect(() => {
    if (vendorTounchList?.length && StockKeepingUnit?.label) {
      const result = vendorTounchList?.filter((item) => {
        if (
          item?.StockKeepingUnit == StockKeepingUnit?.label &&
          item?.VendorId == VendorName?.value &&
          item?.CategoryId == CategoryId?.value &&
          item?.ProductId == ProductId?.value &&
          item?.DesignId == DesignId?.value &&
          item?.PurityId == PurityId?.value
        ) {
          return item;
        }
      });

      const vendorTounch = vendorTounchList?.filter(
        (item) =>
          item?.VendorId == VendorName?.value &&
          item?.StockKeepingUnit == StockKeepingUnit?.label
      );

      console.log(result);
      console.log(vendorTounch);
      if (result?.length && result[0]?.FinePure) {
        setValue("WastageCut", true);
      } else {
        setValue("WastageCut", false);
      }
      if (result?.length) {
        setValue("MakingFixedAmt", result[0]?.MakingFixedAmt);
        setValue("FinePercent", result[0]?.FinePercentage);
        setValue("WastagePercent", result[0]?.MakingPercentage);
        setValue("MakingPerGram", result[0]?.MakingPerGram);
        setValue("MakingFixedWastage", result[0]?.MakingFixedWastage);
        setIsWastageFieldDisabled(true);
      } else {
        setIsWastageFieldDisabled(false);
        setValue("WastagePercent", "");
        setValue("FinePercent", "");
        setValue("MakingFixedAmt", "");
        setValue("WastagePercent", "");
        setValue("MakingPerGram", "");
        setValue("MakingFixedWastage", "");
      }
      let StoneAmount = 0;
      if (
        Stones?.length &&
        vendorTounch?.length &&
        vendorTounch[0]?.Stones?.length
      ) {
        Stones?.forEach((stoneItem) => {
          vendorTounch[0]?.Stones?.forEach((tounchStone) => {
            if (tounchStone?.StoneName == stoneItem?.StoneName) {
              StoneAmount +=
                +(stoneItem?.StoneWeight || 0) * +(tounchStone?.StoneRate || 0);
            }
          });
        });
        setValue("StoneAmount", StoneAmount);
      }
    }

    if (vendorTounchList?.length && !StockKeepingUnit?.label) {
      const result = vendorTounchList?.length
        ? vendorTounchList?.filter((item) => {
            if (
              !item?.StockKeepingUnit &&
              item?.VendorId == VendorName?.value &&
              item?.CategoryId == CategoryId?.value &&
              item?.ProductId == ProductId?.value &&
              item?.DesignId == DesignId?.value &&
              item?.PurityId == PurityId?.value
            ) {
              return item;
            }
          })
        : [];
      const vendorTounch = vendorTounchList?.length
        ? vendorTounchList?.filter(
            (item) =>
              item?.VendorId == VendorName?.value && !item?.StockKeepingUnit
          )
        : [];
      console.log(result);
      console.log(vendorTounch);
      if (result?.length && result[0]?.FinePure) {
        setValue("WastageCut", true);
      } else {
        setValue("WastageCut", false);
      }
      if (result?.length) {
        setValue("MakingFixedAmt", result[0]?.MakingFixedAmt);
        setValue("FinePercent", result[0]?.FinePercentage);
        setValue("WastagePercent", result[0]?.MakingPercentage);
        setValue("MakingPerGram", result[0]?.MakingPerGram);
        setValue("MakingFixedWastage", result[0]?.MakingFixedWastage);
        setIsWastageFieldDisabled(true);
      } else {
        setIsWastageFieldDisabled(false);
        setValue("WastagePercent", "");
        setValue("FinePercent", "");
        setValue("MakingFixedAmt", "");
        setValue("WastagePercent", "");
        setValue("MakingPerGram", "");
        setValue("MakingFixedWastage", "");
      }
      let StoneAmount = 0;
      if (
        Stones?.length &&
        vendorTounch?.length &&
        vendorTounch[0]?.Stones?.length
      ) {
        Stones?.forEach((stoneItem) => {
          vendorTounch[0]?.Stones?.forEach((tounchStone) => {
            if (tounchStone?.StoneName == stoneItem?.StoneName) {
              StoneAmount +=
                +(stoneItem?.StoneWeight || 0) * +(tounchStone?.StoneRate || 0);
            }
          });
        });
        setValue("StoneAmount", StoneAmount);
      }
    }
  }, [
    StockKeepingUnit?.label,
    vendorTounchList,
    VendorName?.value,
    CategoryId?.value,
    ProductId?.value,
    DesignId?.value,
    PurityId?.value,
    Stones,
  ]);

  useEffect(()=>{
    if(dropdownListData?.productTypes?.length){
        const foundProduct = dropdownListData?.productTypes?.find((item)=>item?.Id == DiamondName?.value)
        console.log(foundProduct)
        if(foundProduct?.Id){
          setValue("DiamondShape",{value:foundProduct?.Shape,label:foundProduct?.ShapeName})
          setValue("DiamondColour",{value:foundProduct?.Color,label:foundProduct?.ColorName})
          setValue("DiamondClarity",{value:foundProduct?.Clarity,label:foundProduct?.ClarityName})
        }else{
          setValue("DiamondShape","")
          setValue("DiamondColour","")
          setValue("DiamondClarity","")
        }
    }
  },[DiamondName,dropdownListData?.productTypes])

  // --------------calculations starts here --------------

  useEffect(() => {
    // if (CategoryId?.label !== "LOOSE DIAMOND") {
    const grossWeight = +TotalWt - +PackingWeight;
    console.log(grossWeight)
    console.log(PackingWeight)
    const netWeight = +grossWeight - +StoneWt - +DiamondWeight;
    const fineWeight = (+FinePercent * +netWeight) / 100;
    const wastageWeight =
      // +changeWastageWt
      // ?
      //  (+WastagePercent * +fineWeight) / 100
      // :
      WastageCut
        ? (+(WastagePercent || 0) * +(fineWeight || 0)) / 100
        : (+WastagePercent * +netWeight) / 100;

    const fineWastageWeight = WastageCut
      ? +fineWeight +
        (+(WastagePercent || 0) * +(fineWeight || 0)) / 100 +
        +MakingFixedWastage
      : +fineWeight + +wastageWeight + +MakingFixedWastage;

    // const makingPerGram = +netWeight * +MetalRate;
    const totalMakingAmount =
      (+WastagePercent / 100) * +netWeight * +MetalRate +
      +MakingPerGram * netWeight +
      +MetalRate * +MakingFixedWastage +
      +MakingFixedAmt;

    const metalAmount = +MetalRate * +netWeight;
    const totalItemAmount =
      +metalAmount +
      +totalMakingAmount +
      +StoneAmount +
      +Hallmark +
      +DiamondAmount; // need to add diamond amount

    // Update all values
    setValue("GrossWt", (+(grossWeight || 0))?.toFixed(3));
    setValue("NetWt", netWeight?.toFixed(3));
    setValue("FineWt", fineWeight?.toFixed(3));
    // setValue("WastageWt", wastageWeight?.toFixed(3));
    setValue("FineWastageWt", fineWastageWeight?.toFixed(3));
    if (Qty) {
      setValue("ClipQty", Qty);
      if(Stones?.length){
        let amount = 0;
        let pieces = 0;
        let stoneWt = 0;
        Stones?.forEach((item) => {
          amount = amount + +(item?.StoneWeight || 0) * (+(item?.StoneRateKarate || 0) || +(item?.StoneRatePiece || 0)) 
          pieces = +pieces + +item?.StonePieces;
          stoneWt = +stoneWt + +item?.StoneWeight;
        });
        if(pieces && Qty){
          setValue("StonePieces",(+(pieces || 0) * +(Pieces || 1)) * +(Qty || 0));
        }
        if (pieces && !Qty) {
        setValue("StonePieces",  +(pieces || 0) * +(Pieces || 1));
        }
        if(stoneWt && Qty){
          setValue("StoneWt", (+(stoneWt || 0) * +(Pieces || 1)) * +(Qty || 0));
        }
        if(stoneWt && !Qty){
          setValue("StoneWt", (+(stoneWt || 0) * +(Pieces || 1)));
        }
      }
    }
    // if (PackingWeight) {
    //   setValue("ClipQty", "");
    //   setValue("ClipWt", "");
    //   setValue("TagWt", "");
    //   setValue("FindingWt", "");
    //   setValue("LanyardWt", "");
    // }

    // setValue("MakingPerGram", makingPerGram);

    if (RateCut) {
      setValue("TotalMakingAmount", totalMakingAmount?.toFixed(3));
      setValue("TotalItemAmount", totalItemAmount?.toFixed(3));
      setValue("MetalAmount", metalAmount?.toFixed(3));
      setValue("FineWt", "");
      setValue("WastageWt", "");
      setValue("FineWastageWt", "");
    } else {
      setValue(
        "TotalMakingAmount",
        (+MakingPerGram * +netWeight + +MakingFixedAmt)?.toFixed(3)
      );
      setValue(
        "TotalItemAmount",
        (
          +MakingPerGram * +netWeight +
          +MakingFixedAmt +
          +Hallmark +
          +StoneAmount +
          +DiamondAmount
        ).toFixed(3)
      );
      setValue("MetalAmount", "");
      setValue("FineWt", fineWeight?.toFixed(3));
      setValue("WastageWt", (+wastageWeight + +MakingFixedWastage)?.toFixed(3));
      setValue("FineWastageWt", fineWastageWeight?.toFixed(3));
    }


    // + +StoneAmount + +Diamo?ndAmount).toFixed(3))
    // }
  }, [
    TotalWt,
    PackingWeight,
    StoneWt,
    DiamondWeight,
    FinePercent,
    WastagePercent,
    changeWastageWt,
    changeRate,
    MetalRate,
    WastageWt,
    MakingFixedWastage,
    MakingFixedAmt,
    StoneAmount,
    RateCut,
    WastageCut,
    MakingPerGram,
    Hallmark,
    DiamondAmount,
    Qty,
    Pieces,
    FineWt,
    
  ]);

  // useEffect(()=>{

  //   const pkgWt = (+(ClipQty || 0) * +(ClipWt || 1)) + +(PouchWt || 0) 
  //   setValue("PackingWeight",(+(pkgWt || 0))?.toFixed(3))
     
  // },[ClipQty,ClipWt,PouchWt])

  useEffect(() => {
    if (+LooseDiamondWeight && +LooseDiamondRate) {
      setValue("DiamondAmount", +LooseDiamondWeight * +LooseDiamondRate);
    }
  }, [LooseDiamondWeight, LooseDiamondRate]);

  useEffect(() => {
    if (+StoneRateCt && +LooseStoneWeight) {
      setValue("StoneAmount", +StoneRateCt * +LooseStoneWeight);
    }
    if (+StoneRatePiece && +LooseStonePieces) {
      setValue("StoneAmount", +StoneRatePiece * +LooseStonePieces);
    }
  }, [LooseStoneWeight, StoneRateCt, LooseStonePieces, StoneRatePiece]);

  // useEffect(()=>{
  //   if(TotalWt && PackingWeight && isGrossWeightChanged){
  //     setValue("PackingWeight",+TotalWt - +GrossWt)
  //     setValue("GrossWt",+TotalWt - +PackingWeight)
  //   }
  // },[isGrossWeightChanged])

  // --------------calculations ends here --------------

  const addDiamond = (data) => {
    let amount = 0;
    let diamondWt = 0;

    data?.diamonds?.forEach((item) => {
      amount = amount + +item.DiamondRate * +item.DiamondWeight;
      diamondWt = +diamondWt + +item?.DiamondWeight;
    });
    setValue("Diamonds", data?.diamonds);
    setValue("DiamondAmount", amount);
    setValue("DiamondWeight", diamondWt);
    setPopUp({ status: false, label: "" });
  };
  const addStone = (data) => {
    let amount = 0;
    let pieces = 0;
    let stoneWt = 0;
    data?.stones?.forEach((item) => {
      amount = amount + +(item?.StoneWeight || 0) * (+(item?.StoneRateKarate || 0) || +(item?.StoneRatePiece || 0)) 
      pieces = +pieces + +item?.StonePieces;
      stoneWt = +stoneWt + +item?.StoneWeight;
    });
    setValue("StoneAmount", amount);
    if(pieces && Qty){
      setValue("StonePieces", (+(pieces || 0) * +(Pieces || 1)) * +(Qty || 0));
    }
    if (pieces && !Qty) {
    setValue("StonePieces", +(pieces || 0) * +(Pieces || 1));
    }
    if(stoneWt && Qty){
      setValue("StoneWt", (+(stoneWt || 0) * +(Pieces || 1)) * +(Qty || 0));
    }
    if(stoneWt && !Qty){
      setValue("StoneWt", (+(stoneWt || 0) * +(Pieces || 1)));
    }

    setValue("Stones", data?.stones);
    setPopUp({ status: false, label: "" });
  };
  const handleAddSku = () => {
    handleSubmit(onSubmit)();
  };
  const onSubmit = (data) => {
    if (edit) {
      updateSku(data);
    } else {
      addSku(data);
    }
  };

  const inputRefs = useRef([]);

  const handleKeyDown = (e, index, isDropdownOpen) => {
    // if (e.key === "Enter") {
    //   e.preventDefault();
    //   // Check if the dropdown is open
    //   if (isDropdownOpen) {
    //     // If the dropdown is open and an option is selected, allow selecting the item
    //     return;
    //   }
    //   // Loop through the next fields to find the next enabled field
    //   let nextIndex = index + 1;
    //   // Skip over disabled fields (input or Select dropdown)
    //   while (
    //     inputRefs.current[nextIndex] &&
    //     inputRefs.current[nextIndex].disabled
    //   ) {
    //     nextIndex++;
    //   }
    //   // If a valid next field exists, focus it
    //   if (inputRefs.current[nextIndex]) {
    //     inputRefs.current[nextIndex].focus();
    //   }
    // }
  };

  return (
    <>
      {popup?.status && popup?.label == "Diamond" ? (
        <SingleDiamondPopUpNew
          onClose={() => setPopUp({ status: false, label: "" })}
          dropdownListData={dropdownListData}
          addDiamond={addDiamond}
          Diamonds={Diamonds}
          diamondEditValues={{}}
          diamondEdit={false}
          setFormValue={setValue}
        />
      ) : (
        ""
      )}

      {popup?.status && popup?.label == "Stone" ? (
        <SingleStonePopUpNewForPurchase
          onClose={() => setPopUp({ status: false, label: "" })}
          dropdownListData={dropdownListData}
          addStone={addStone}
          Stones={Stones}
          StonePieces={StonePieces}
          setFormValue={setValue}
          StockKeepingUnit={StockKeepingUnit}
          selectedSku={selectedSku}
          VendorTounchList={vendorTounchList}
          VendorName={VendorName}
        />
      ) : (
        ""
      )}
      <div className={styles.linesection}>
        <div className={styles.middlecontainer}>
          {itemfields
            ?.filter((field) => field?.place === "middle" && field.IsVisible)
            ?.sort((a, b) => a.priority - b.priority)
            ?.map((field, index) => (
              <div className={styles.box} key={field.key}>
                <label title={field.label}>
                  {field.label.substring(0, 14)}{" "}
                  {field.label?.length > 14 && "..."}
                  {field?.mandatory && <span style={{ color: "red" }}>*</span>}
                </label>
                {field.type === "inputselect" ? (
                  // SKU Input with datalist
                  <input
                    type="text"
                    name={field.key}
                    disabled={field.visibility === "ReadOnly"}
                    {...register(field.key, {
                      required:
                        field?.mandatory &&
                        field.visibility !== "ReadOnly" &&
                        `${field.label} is required`,
                    })}
                    list="skuList"
                    autoComplete="off"
                    style={{ background: "white", border: "1px solid #ddd" }}
                    onKeyDown={(e) => {
                      if (e?.code == "Enter") {
                        e.preventDefault(); // Prevent form submission
                      }
                    }}
                  />
                ) : field.type === "dropdown" ||
                  field.type === "searchDropdown" ? (
                  <>
                    <Controller
                      name={field.key}
                      control={control}
                      rules={{
                        required:
                          field?.mandatory &&
                          field.visibility !== "ReadOnly" &&
                          `${field.label} is required`,
                        validate: (value) => {
                          if (
                            field?.mandatory &&
                            field.visibility !== "ReadOnly" &&
                            value?.value === ""
                          ) {
                            return `${field.label} cannot be empty`;
                          }
                        },
                      }}
                      render={({ field: controllerField }) => {
                        return (
                          <Select
                            {...controllerField}
                            options={generateDropDownList({
                              field,
                              dropdownListData,
                              CategoryId,
                              ProductId,
                              DesignId,
                              PurityId,
                              skus,
                              VendorName
                            })?.map((item) => ({
                              value: item?.value,
                              label: item?.label,
                            }))}
                            isDisabled={
                              field.visibility === "ReadOnly" ||
                              ((field.key == "CategoryId" ||
                                field.key == "ProductId" ||
                                field.key == "DesignId" ||
                                field.key == "PurityId") &&
                                StockKeepingUnit?.value)
                            }
                            isSearchable={field.type === "searchDropdown"}
                            styles={selectStyle}
                            isClearable={true}
                            // onKeyDown={(e) => {
                            //   if(e?.code == "Enter"){
                            //     e.preventDefault(); // Prevent form submission
                            //   }

                            // }}
                          />
                        );
                      }}
                    />
                  </>
                ) : field.type === "number" || field.type === "text" ? (
                  // Number input field
                  <div style={{ position: "relative" }}>
                    {field.key == "WastagePercent" && (
                      <FiRefreshCcw
                        onClick={() => {
                          if (WastageCut) {
                            setValue("WastageCut", false);
                          } else {
                            setValue("WastageCut", true);
                          }
                        }}
                        style={{
                          color: `${WastageCut ? "#0077d4" : "#627282"}`,
                          cursor: "pointer",
                          position: "absolute",
                          right: "12px",
                          top: "6px",
                        }}
                        size={20}
                        title="Wastage On Fine Wt"
                      />
                    )}

                    {field.key == "MetalRate" && (
                      <FaDollarSign
                        onClick={() => {
                          if (RateCut) {
                            setValue("RateCut", false);
                          } else {
                            setValue("RateCut", true);
                          }
                        }}
                        style={{
                          color: `${RateCut ? "#0077d4" : "#627282"} `,
                          cursor: "pointer",
                          position: "absolute",
                          right: "4px",
                          top: "6px",
                          marginRight: 5
                        }}
                        size={20}
                        title="Rate Cut"
                      />
                    )}

                    <input
                      // type={field.type}
                      type={
                        field.visibility === 'Mask' ? 'password' : field.type
                      }
                      name={field.key}
                      step="0.001"
                      disabled={
                        field.visibility === "ReadOnly" ||
                        (field.key == "ClipQty" && Qty) ||
                        ((+PackingWeight || +TotalWt) &&
                          field.key == "GrossWt") 
                          ||
                        // (+(PackingWeight || 0) != 0 &&
                        //   (field.key == "ClipQty" ||
                        //     field.key == "ClipWt" ||
                        //     field.key == "TagWt" ||
                        //     field.key == "FindingWt" ||
                        //     field.key == "LanyardWt")) ||
                        (field.key == "WastagePercent" &&
                          isWastageFieldDisabled) ||
                        (+StoneRateCt && field.key == "StoneRatePc") ||
                        (+StoneRatePiece && field.key == "StoneRateCt")
                      }
                      {...register(field.key, {
                        required:
                          field?.mandatory &&
                          field.visibility !== "ReadOnly" &&
                          `${field.label} is required`,
                        pattern:
                          field.type === "number"
                            ? /^\d+(\.\d{0,3})?$/
                            : undefined, // Validate input
                        validate: (value) => {
                          if (
                            field.key == "PackingWeight" &&
                            +(PackingWeight || 0) > +(TotalWt || 0)
                          ) {
                            return "Packing weight cannot exceed total weight.";
                          }
                          if (field.key == "GrossWt" && +(GrossWt || 0) > +(TotalWt || 0)) {
                            return "Gross weight cannot exceed total weight.";
                          }
                        },
                      })}
                      style={{
                        background: `${
                          field.visibility == "ReadOnly" ||
                          (field.key == "ClipQty" && Qty) ||
                          field.key == "GrossWt" ||
                          (+StoneRateCt && field.key == "StoneRatePc") ||
                          (+StoneRatePiece && field.key == "StoneRateCt") 
                          // ||
                          // (+(PackingWeight || 0) != 0 &&
                          //   (field.key == "ClipQty" ||
                          //     field.key == "ClipWt" ||
                          //     field.key == "TagWt" ||
                          //     field.key == "FindingWt" ||
                          //     field.key == "LanyardWt"))
                          //   ? "#f0f0f0"
                          //   : "white"

                        }`,
                        border: "1px solid #ddd",

                        height: '35px'
                      }}
                      onKeyDown={(e) => {
                        if (e?.code == "Enter") {
                          e.preventDefault(); // Prevent form submission
                        }
                      }}
                    />
                  </div>
                ) : field.type === "popup" ? (
                  <div>
                    <div
                      className="input-group"
                      style={{ position: "relative" }}
                    >
                      <input
                        id="stone-input"
                        type="text"
                        style={{ margin: 0 , height: '35px'}}
                        className="input-field"
                        onKeyDown={(e) => {
                          if (e?.code == "Enter") {
                            e.preventDefault(); // Prevent form submission
                            setPopUp({ status: true, label: field.key });
                          }
                        }}
                        disabled={
                          (field?.key == "Stone" && StoneWt) ||
                          (field?.key == "Diamond" && DiamondWeight)
                        }
                      />
                      <Add
                        type="button"
                        size={28}
                        onClick={() => {
                          setPopUp({ status: true, label: field.key });
                        }}
                        style={{
                          background: "#A9A9A9",
                          cursor: "pointer",
                          height: "100%",
                          color: "white",
                        }}
                        disabled={StoneWt}
                      />
                      {/* <button
                        // className="add-button"
                        style={{position:"absolute"}}
                        type="button"
                        onClick={() => {
                          setPopUp({ status: true, label: field.key });
                        }}
                      >
                        +
                      </button> */}
                    </div>
                  </div>
                ) : null}
                {errors[field.key] && (
                  <span className={styles.fielderror}>
                    {errors[field.key].message}
                  </span>
                )}
              </div>
            ))}
        </div>

        <div className={styles.custombarlinehead}>
          {isInvoiceEntry ? (
            <div></div>
          ) : (
            // <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            //   <label>Add this product to unlabeled stock</label>
            //   <div className={styles.switch}>
            //     <input
            //       type="checkbox"
            //       id="toggleSwitch"
            //       className={styles.switchinput}
            //       {...register("unlabeled")}
            //     />
            //     <label
            //       className={styles.switchlabel}
            //       htmlFor="toggleSwitch"
            //     ></label>
            //   </div>
            // </div>
            ''
          )}

          <div style={{ display: "flex", alignItems: "center" }}>
            <button
              type="button"
              style={{
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "14px",
                backgroundColor: "#0077d4",
              }}
              onClick={handleAddSku}
            >
              <Icon style={{ fontSize: "24px" }} icon="fluent:add-32-filled" />
              Add
            </button>
            <button
              type="button"
              style={{
                fontSize: "14px",
                backgroundColor: "#0077d4",
                padding: "4px 8px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
              }}
              onClick={() => setIsSummaryOpen(!isSummaryOpen)}
            >
              <Icon style={{ fontSize: "24px" }} icon="solar:list-bold" />
              Summary
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SkuFormRepair;
